import { useConfirmationModal } from '@thedealersconcierge/components';
import { useState } from 'react';
import { toast } from 'react-toastify';
import Button from '~/components/Button';
import Modal from '~/components/Modal';
import { gqlMutationClient } from '~/lib/backend';
import { queryClient } from '~/lib/query';
import { useModals, useParams } from '~/router';

const DealScenariosPage = () => {
  const modals = useModals();
  const { pId } = useParams('/dashboard/transaction/:pId');
  const transactionId = pId; // pId = Public ID (e.g. "tx_..."); aliased for use in queries and mutations
  const [isRemoving, setIsRemoving] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const { showWarningModal, ConfirmationModalComponent } =
    useConfirmationModal();
  const handleRemovePrimaryBuyer = async () => {
    const confirmed = await showWarningModal({
      message:
        'You are about to remove the primary buyer from this transaction. The co-buyer will become the primary buyer. Are you sure you want to proceed?',
      dataTestId: 'remove-primary-buyer-confirmation'
    });

    if (!confirmed) {
      return;
    }

    setIsRemoving(true);

    const resp = await gqlMutationClient()({
      removePrimaryBuyer: [
        {
          transactionId
        },
        {
          __typename: true,
          '...on GraphQLError': {
            message: true
          },
          '...on MutationRemovePrimaryBuyerSuccess': {
            data: {
              status: true
            }
          }
        }
      ]
    });

    if (
      !resp.removePrimaryBuyer ||
      resp.removePrimaryBuyer.__typename === 'GraphQLError'
    ) {
      const message =
        resp.removePrimaryBuyer?.message ??
        'Could not remove primary buyer. Please try again or contact support.';
      console.error(message);
      toast.error(message);
    } else {
      await queryClient.resetQueries({
        queryKey: ['transaction', transactionId]
      });

      modals.close({
        at: '/dashboard/transaction/:pId',
        params: {
          pId
        }
      });
    }

    setIsRemoving(false);
  };
  const handleSwitchRoles = async () => {
    const confirmed = await showWarningModal({
      message:
        'You are about to switch the buyer and co-buyer roles in this transaction. Are you sure you want to proceed?',
      dataTestId: 'switch-roles-confirmation'
    });

    if (!confirmed) {
      return;
    }

    setIsSwitching(true);

    const resp = await gqlMutationClient()({
      switchCustomerRoles: [
        {
          transactionId
        },
        {
          __typename: true,
          '...on GraphQLError': {
            message: true
          },
          '...on MutationSwitchCustomerRolesSuccess': {
            data: {
              status: true
            }
          }
        }
      ]
    });

    if (resp.switchCustomerRoles?.__typename === 'GraphQLError') {
      const message =
        resp.switchCustomerRoles.message ??
        'Could not make co-buyer primary buyer. Please try again or contact support.';
      console.error(message);
      toast.error(message);
    } else {
      await queryClient.resetQueries({
        queryKey: ['transaction', transactionId]
      });

      modals.close({
        at: '/dashboard/transaction/:pId',
        params: {
          pId
        }
      });
    }

    setIsSwitching(false);
  };

  return (
    <>
      <Modal
        title="Deal Scenarios"
        className="h-fit grow-0"
        isOpen
        onClose={modals.close}
      >
        <div className="flex flex-col space-y-8 p-20">
          <div className="flex flex-col space-y-8 items-end p-8 bg-very-light-blue rounded-lg">
            <div className="flex flex-col space-y-4 w-full">
              <h2 className="text-heading-2">Scenario 1: Switch Roles</h2>

              <div className="text-body">
                When selected, the buyer & co-buyer will switch roles in the
                same deal transaction.
              </div>
            </div>

            <Button
              onClick={() => {
                void handleSwitchRoles();
              }}
              loading={isSwitching}
              disabled={isRemoving}
            >
              Switch Roles
            </Button>
          </div>

          <div className="flex flex-col space-y-8 items-end p-8 bg-very-light-blue rounded-lg">
            <div className="flex flex-col space-y-4 w-full">
              <h2 className="text-heading-2">
                Scenario 2: Remove Primary Buyer
              </h2>

              <div className="text-body">
                When selected, the primary buyer will be removed from the
                transaction and the co-buyer becomes the primary buyer.
              </div>
            </div>

            <Button
              variant="DESTRUCTIVE"
              onClick={() => {
                void handleRemovePrimaryBuyer();
              }}
              loading={isRemoving}
              disabled={isSwitching}
            >
              Remove Primary Buyer
            </Button>
          </div>
        </div>
      </Modal>

      {ConfirmationModalComponent}
    </>
  );
};

export default DealScenariosPage;
